import { getIntlLocale, formatFixed, currencyDisplayFor } from "@/lib/locale";

/**
 * Formatting utilities for financial data.
 */

/**
 * Returns the currency symbol for a given currency code.
 * Returns an empty string if the code is null, undefined, or unsupported.
 */
export function getSymbol(currencyCode: string | null | undefined): string {
  if (!currencyCode) return "";
  try {
    const parts = new Intl.NumberFormat(getIntlLocale("en-US"), {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: currencyDisplayFor(getIntlLocale("en-US"), currencyCode),
    }).formatToParts(0);
    return parts.find((p) => p.type === "currency")?.value ?? "";
  } catch {
    return "";
  }
}

/**
 * Formats large financial numbers (Trillions, Billions, Millions).
 */
export function fmt(
  value: number | null | undefined,
  currencyCode?: string | null,
): string {
  if (value == null) return "—";
  const sym = getSymbol(currencyCode);
  const absValue = Math.abs(value);
  if (absValue >= 1e12) return `${sym}${formatFixed(value / 1e12, 2)}T`;
  if (absValue >= 1e9) return `${sym}${formatFixed(value / 1e9, 2)}B`;
  if (absValue >= 1e6) return `${sym}${formatFixed(value / 1e6, 0)}M`;
  return `${sym}${value.toLocaleString(getIntlLocale(undefined))}`;
}

/**
 * Formats EPS (Earnings Per Share) with 2 decimal places.
 */
export function fmtEps(
  value: number | null | undefined,
  currencyCode?: string | null,
): string {
  if (value == null) return "—";
  const sym = getSymbol(currencyCode);
  return `${sym}${formatFixed(value, 2)}`;
}

/**
 * Formats share counts (Billions, Millions).
 * TECH-DEBT: fmtShares is exported but not imported in any component; only referenced in
 *   formatters.test.ts. Remove export (or the function) if no feature is planned to use it.
 */
export function fmtShares(value: number | null | undefined): string {
  if (value == null) return "—";
  const absValue = Math.abs(value);
  if (absValue >= 1e9) return `${formatFixed(value / 1e9, 2)}B`;
  if (absValue >= 1e6) return `${formatFixed(value / 1e6, 2)}M`;
  return value.toLocaleString(getIntlLocale(undefined));
}

/**
 * Generates a standard period label (e.g., "Q4 '25").
 */
export function periodLabel(
  periodEnd: string,
  quarter: number,
  year: number,
): string {
  return `Q${quarter} '${String(year).slice(2)}`;
}

/**
 * Strips common legal entity suffixes from a company name.
 * e.g. "Apple Inc." → "Apple", "Microsoft Corporation" → "Microsoft"
 */
export function cleanCompanyName(name: string): string {
  if (!name) return name;
  const suffixes = [
    /(?:,\s*|\s+)and\s+Company$/i,
    /(?:,\s*|\s+)and\s+Co\.?$/i,
    /(?:,\s*|\s+)Inc\.?$/i,
    /(?:,\s*|\s+)Incorporated$/i,
    /(?:,\s*|\s+)Corp\.?$/i,
    /(?:,\s*|\s+)Corporation$/i,
    /(?:,\s*|\s+)Co\.?$/i,
    /(?:,\s*|\s+)Company$/i,
    /(?:,\s*|\s+)Ltd\.?$/i,
    /(?:,\s*|\s+)Limited$/i,
    /(?:,\s*|\s+)L\.?L\.?C\.?$/i,
    /(?:,\s*|\s+)PLC$/i,
    /(?:,\s*|\s+)S\.?A\.?$/i,
    /(?:,\s*|\s+)N\.?V\.?$/i,
    /(?:,\s*|\s+)AG$/i,
    /(?:,\s*|\s+)AB$/i,
    /(?:,\s*|\s+)GmbH$/i,
  ];
  let cleaned = name;
  for (const suffix of suffixes) cleaned = cleaned.replace(suffix, "");
  return cleaned.replace(/,\s*$/, "").trim();
}
